import type {
    DiscoveryContent,
    Content as MarketingContent,
} from "@amzn/ads-discovery-theme-service-typescript-client";
import type { TemplateAssociations } from "@amzn/d16-g-cdb-publisher-portal-typescript-client";
import type { InsightFamily } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { ContentType, type SelectedContent } from "@amzn/g4sservice-client";
import { cloneDeep } from "lodash";

import type { SelectedContentInsight } from "../types/selectedContentInsight";
import { SelectedContentType } from "../types/selectedContentInsight";

export function convertToInsightFamily(
    content: SelectedContent,
    advertiserId?: string,
): InsightFamily | undefined {
    let insightFamily: InsightFamily | undefined = undefined;
    try {
        insightFamily = {
            identifier: content.content!["familyId"],
            title: content.title,
            advertiserId,
            brand: content.metadata && content.metadata["brandName"],
            brandId: content.metadata && content.metadata["brandId"],
            browseNode: content.metadata && content.metadata["browseNodeName"],
            browseNodeId: content.metadata && content.metadata["browseNodeId"],
            advertiser: undefined,
            familyName: content.metadata!["insightFamilyName"],
            metadata: content.metadata,
        };
    } catch (e) {
        console.error(
            `Invalid Insight Family - ${JSON.stringify(content, null, 4)}`,
        );
    }
    return insightFamily;
}

export function convertToOnDemandInsight(
    content: SelectedContent,
    templateAssociations: TemplateAssociations[],
): TemplateAssociations | undefined {
    let templateAssociation: TemplateAssociations | undefined = undefined;
    try {
        templateAssociation = cloneDeep(
            templateAssociations.find((templateAssociation) => {
                return (
                    templateAssociation.id === content.content!["templateId"]
                );
            }),
        );
    } catch (e: any) {
        console.error(
            `Invalid Template ${JSON.stringify(content, null, 4)} - ${e.message}`,
        );
    }

    return templateAssociation;
}

export function convertToMarketingNarrative(
    content: SelectedContent,
    marketingNarratives: DiscoveryContent[],
): MarketingContent | undefined {
    let marketingNarrative: DiscoveryContent | undefined = undefined;
    try {
        marketingNarrative = cloneDeep(
            marketingNarratives.find((marketingContent) => {
                return (
                    marketingContent.contentId ===
                    content.content!["documentId"]
                );
            }),
        );
        return marketingNarrative;
    } catch (e: any) {
        console.error(
            `Invalid Marketing Narrative ${JSON.stringify(content, null, 4)} - ${e.message}`,
        );
    }

    return undefined;
}

export const convertToSelectedContentInsight = (
    content: SelectedContent,
    templateAssociations: TemplateAssociations[],
    marketingNarratives: DiscoveryContent[],
): SelectedContentInsight | undefined => {
    switch (content.type) {
        case ContentType.INSIGHT: {
            const insightFamily = convertToInsightFamily(content);
            return insightFamily
                ? {
                      contentType: SelectedContentType.INSIGHT_WITH_SUMMARY,
                      content: {
                          insightFamily,
                          g4sSummary: content.summary,
                          selectedInsightSubtypeIds:
                              content.content!["familySubtypeIds"],
                      },
                  }
                : undefined;
        }
        case ContentType.TEMPLATE: {
            const template: TemplateAssociations | undefined =
                convertToOnDemandInsight(content, templateAssociations);
            if (!template) {
                console.error(
                    `Invalid Template - ID not found in templateAssociations: ${JSON.stringify(content, null, 4)}`,
                );
                return undefined;
            }
            return {
                contentType: SelectedContentType.TEMPLATE,
                content: template,
                summary: content.summary,
                title: content.title,
            };
        }
        case ContentType.MARKETING_CONTENT: {
            const marketingNarrative = convertToMarketingNarrative(
                content,
                marketingNarratives,
            );
            if (!marketingNarrative) {
                console.error(
                    `Invalid Marketing Narrative - ID not found in marketingNarratives: ${JSON.stringify(content, null, 4)}`,
                );
                return undefined;
            }
            return {
                contentType: SelectedContentType.MARKETING_CONTENT,
                content: marketingNarrative,
                // Content users are not provided within the G4S response.
                contentUsers: [],
                summary: content.summary,
                title: content.title,
            };
        }
        default:
            return undefined;
    }
};

export const convertToSelectedContentInsights = (
    contents: SelectedContent[],
    templateAssociations: TemplateAssociations[],
    marketingNarratives: DiscoveryContent[],
): SelectedContentInsight[] => {
    const selectedContentInsights: SelectedContentInsight[] = [];
    contents.forEach((content) => {
        const converted: SelectedContentInsight | undefined =
            convertToSelectedContentInsight(
                content,
                templateAssociations,
                marketingNarratives,
            );
        if (converted) selectedContentInsights.push(converted);
    });
    return selectedContentInsights;
};
